import { useState, useEffect } from 'react';
import { ClientPageLayout } from '@/components/client/shared/ClientPageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DashboardKPICards } from '@/components/client/DashboardKPICards';
import { ClientDashboardCharts } from '@/components/client/ClientDashboardCharts';
import { Circle, Phone, Users } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useClientBotType } from '@/hooks/useClientBotType';
import { useTraces } from '@/hooks/useTraces';
import { clientApiService, BotResponse } from '@/services/api/client.service';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';

export function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessionStart] = useState(() => new Date());
  const [bot, setBot] = useState<BotResponse | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Get client ID and bot type
  const clientId = user?.client_project_id || '86a999a8-9952-428d-a273-83efcb6330fd';
  const { botType, isVoicebot, isChatbot } = useClientBotType();
  
  // Get traces data for real dashboard analytics
  const { data: tracesData, isLoading: tracesLoading } = useTraces({
    page: 1,
    limit: 100 // Get more data for better analytics
  });
  
  useEffect(() => {
    fetchBotData();
  }, [clientId]);
  
  const fetchBotData = async () => {
    try {
      setLoading(true);
      const bots = await clientApiService.getClientBots(clientId);
      if (bots && bots.length > 0) {
        setBot(bots[0]); // Get the first bot
      }
    } catch (err) {
      console.error('Error fetching bot data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const formatSessionTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (latency: number): string => {
    if (latency < 1000) return `${latency}ms`;
    return `${(latency / 1000).toFixed(1)}s`;
  };

  return (
    <ClientPageLayout
      title="Dashboard"
      subtitle="Resumen de rendimiento de tu asistente"
      actions={
        <Badge variant="secondary" className="animate-pulse">
          BETA
        </Badge>
      }
    >
      {/* Welcome Section */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-primary">
                Welcome back, {user?.name || 'User'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Session started at {formatSessionTime(sessionStart)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estado General - Muy visual */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="w-16 h-16 rounded-full" />
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-blue-900">
                  Estado de tu Asistente
                </h2>
                {bot ? (
                  <>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-2xl font-bold text-blue-900">
                        {bot.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge 
                        variant="outline" 
                        className={
                          bot.status === 'active' 
                            ? "text-green-600 border-green-600 bg-green-50" 
                            : bot.status === 'maintenance'
                            ? "text-yellow-600 border-yellow-600 bg-yellow-50"
                            : "text-gray-600 border-gray-600 bg-gray-50"
                        }
                      >
                        <Circle className="w-2 h-2 fill-current mr-1" />
                        {bot.status === 'active' ? 'Activo ✨' : 
                         bot.status === 'maintenance' ? 'En mantenimiento 🔧' : 'Inactivo'}
                      </Badge>
                      {bot.metrics && bot.metrics.total_calls > 0 && (
                        <span className="text-sm text-blue-700">
                          {bot.metrics.total_calls} conversaciones hoy
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xl font-medium text-gray-600 mt-2">
                      Sin asistente configurado
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Contacta a tu administrador
                    </p>
                  </>
                )}
              </div>
              <div className="text-6xl">🤖</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPIs Mejoradas con Datos Reales - Solo para voicebots */}
      {isVoicebot && (
        <DashboardKPICards 
          traces={tracesData?.data || []} 
          isLoading={tracesLoading || loading} 
          showCosts={false} // No mostrar costos al cliente
        />
      )}

      {/* KPIs para chatbots mantienen el formato original */}
      {isChatbot && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-6xl mb-4">😊</div>
                <h3 className="text-2xl font-bold">92%</h3>
                <p className="text-muted-foreground">Usuarios satisfechos</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-2xl font-bold">127</h3>
                <p className="text-muted-foreground">Conversaciones hoy</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-6xl mb-4">⚡</div>
                <h3 className="text-2xl font-bold">1.2s</h3>
                <p className="text-muted-foreground">Tiempo respuesta</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Button - Navigate to appropriate section */}
      <div className="flex justify-center">
        <Button
          onClick={() => navigate(isVoicebot ? '/traces' : '/leads')}
          className="flex items-center gap-2"
          size="lg"
        >
          {isVoicebot ? (
            <>
              <Phone className="w-5 h-5" />
              Ver Historial de Llamadas
            </>
          ) : (
            <>
              <Users className="w-5 h-5" />
              Ver Mis Leads
            </>
          )}
        </Button>
      </div>

      {/* Análisis y Gráficas - Solo para voicebots */}
      {isVoicebot && (
        <ClientDashboardCharts 
          traces={tracesData?.data || []} 
          isLoading={tracesLoading}
        />
      )}

      {/* Secciones simplificadas para chatbots */}
      {isChatbot && (
        <div className="grid gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-4">¿Cómo está funcionando mi bot?</h3>
                <div className="text-6xl mb-4">📈</div>
                <p className="text-sm text-muted-foreground">
                  Tu bot resolvió el <strong>92%</strong> de las consultas sin necesidad de intervención humana.
                  Esto te ahorró aproximadamente <strong>120 horas</strong> de trabajo este mes.
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-4">¿Qué dicen tus usuarios?</h3>
                <div className="text-6xl mb-4">💬</div>
                <p className="text-sm text-muted-foreground">
                  El <strong>78%</strong> de los usuarios obtuvieron una respuesta completa en menos de 30 segundos. 
                  Tu bot es especialmente efectivo con consultas sobre horarios y precios.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </ClientPageLayout>
  );
}